import { Connection } from './connection'
import { SFUNegotiator, getLocalStream } from './sfu'
import { SignalAPI } from './api'
import { MessageType, type Message, type ICEServer, type IncomingCallData, type ActiveCallInfo, type ErrorData, type SFUJoinNotifyData } from './protocol'
import { Events } from './events'
import { TypedEventEmitter } from './events'
import { wsUrlToHttpBase } from './utils'
import type { SignalClientOptions, CallStateType, ConnectionState, ClientStateSnapshot } from './types'

export class SignalClient extends TypedEventEmitter {
  private options: Required<Omit<SignalClientOptions, 'meta' | 'iceServers' | 'mediaConstraints' | 'heartbeatTimeout'>> & {
    meta: Record<string, string>
    iceServers: ICEServer[]
    mediaConstraints: MediaStreamConstraints
    heartbeatTimeout: number
  }
  private connection: Connection
  private sfu: SFUNegotiator
  private _api: SignalAPI

  private _isRegistered = false
  private _uid: number = 0
  private _callState: CallStateType = 'idle'
  private _activeCall: ActiveCallInfo | null = null
  private _incomingCall: IncomingCallData | null = null
  private _sfuIceServers: ICEServer[] = []
  private _manualLocalStream: MediaStream | null = null
  private _glareRetryPending = false

  private _reconnectRestoreTimer: ReturnType<typeof setTimeout> | null = null

  constructor(options: SignalClientOptions) {
    super()

    this.options = {
      serverUrl: options.serverUrl,
      role: options.role,
      id: options.id,
      name: options.name,
      password: options.password ?? '',
      meta: options.meta ?? {},
      autoReconnect: options.autoReconnect ?? true,
      heartbeatInterval: options.heartbeatInterval ?? 25000,
      reconnectInterval: options.reconnectInterval ?? 3000,
      heartbeatTimeout: options.heartbeatTimeout ?? 60000,
      iceServers: options.iceServers ?? [],
      enableSFU: options.enableSFU ?? true,
      mediaConstraints: options.mediaConstraints ?? { audio: true, video: true },
    }

    this.connection = new Connection({
      url: this.options.serverUrl,
      autoReconnect: this.options.autoReconnect,
      heartbeatInterval: this.options.heartbeatInterval,
      reconnectInterval: this.options.reconnectInterval,
      heartbeatTimeout: this.options.heartbeatTimeout,
    })

    this.sfu = new SFUNegotiator()
    this._api = new SignalAPI(wsUrlToHttpBase(this.options.serverUrl))

    this.setupConnectionEvents()
    this.setupSFUEvents()
  }

  // ========== 公开属性 ==========

  get isConnected(): boolean {
    return this.connection.isConnected
  }

  get connectionState(): ConnectionState {
    return this.connection.state
  }

  get isRegistered(): boolean {
    return this._isRegistered
  }

  get uid(): number {
    return this._uid
  }

  get callState(): CallStateType {
    return this._callState
  }

  get activeCall(): ActiveCallInfo | null {
    return this._activeCall
  }

  get incomingCall(): IncomingCallData | null {
    return this._incomingCall
  }

  get api(): SignalAPI {
    return this._api
  }

  /** 获取 SDK 内部状态快照 */
  getState(): ClientStateSnapshot {
    return {
      connectionState: this.connection.state,
      isRegistered: this._isRegistered,
      uid: this._uid,
      callState: this._callState,
      activeCall: this._activeCall,
      incomingCall: this._incomingCall,
    }
  }

  // ========== 连接 ==========

  async connect(): Promise<void> {
    await this.connection.connect()
  }

  disconnect(): void {
    if (this._reconnectRestoreTimer) {
      clearTimeout(this._reconnectRestoreTimer)
      this._reconnectRestoreTimer = null
    }
    this.sfu.leave()
    this.connection.disconnect()
    this._isRegistered = false
    this._callState = 'idle'
    this._activeCall = null
    this._incomingCall = null
    this._sfuIceServers = []
    this._manualLocalStream = null
  }

  // ========== 通话操作 ==========

  call(): void {
    this.connection.send({ type: MessageType.CALL })
  }

  initiateCall(deviceId: string): void {
    this.connection.send({
      type: MessageType.INITIATE_CALL,
      data: { device_id: deviceId },
    })
  }

  accept(callId: string): void {
    this.connection.send({
      type: MessageType.ACCEPT,
      data: { call_id: callId },
    })
  }

  reject(callId: string): void {
    this.connection.send({
      type: MessageType.REJECT,
      data: { call_id: callId },
    })
  }

  hangup(callId: string, reason = 'normal'): void {
    if (this.options.enableSFU) {
      this.connection.send({
        type: MessageType.SFU_LEAVE,
        data: { call_id: callId },
      })
    }
    this.connection.send({
      type: MessageType.HANGUP,
      data: { call_id: callId, reason },
    })
    this.sfu.leave()
    this._callState = 'idle'
    this._activeCall = null
    this._incomingCall = null
    this._sfuIceServers = []
  }

  // ========== 媒体控制 ==========

  setLocalStream(stream: MediaStream): void {
    this._manualLocalStream = stream
  }

  getLocalStream(): MediaStream | null {
    return this.sfu.getLocalStream() ?? this._manualLocalStream
  }

  getRemoteStream(): MediaStream | null {
    return this.sfu.getRemoteStream()
  }

  muteAudio(muted: boolean): void {
    this.sfu.muteAudio(muted)
  }

  muteVideo(muted: boolean): void {
    this.sfu.muteVideo(muted)
  }

  // ========== 信令透传 ==========

  sendSignalingMessage(msg: Message): boolean {
    return this.connection.send(msg)
  }

  // ========== 内部方法 ==========

  private sendRegister(): void {
    const data: Record<string, unknown> = {
      role: this.options.role,
      id: this.options.id,
      name: this.options.name,
    }
    if (this.options.password) {
      data.password = this.options.password
    }
    if (Object.keys(this.options.meta).length > 0) {
      data.meta = this.options.meta
    }
    this.connection.send({
      type: MessageType.REGISTER,
      data,
    })
  }

  private setupConnectionEvents(): void {
    this.connection.on('open', () => {
      this.emit(Events.CONNECTED, undefined)
      this.sendRegister()
    })

    this.connection.on('close', (...args: unknown[]) => {
      const [code, reason] = args as [number, string]
      this._isRegistered = false
      this._glareRetryPending = false
      this.emit(Events.DISCONNECTED, { code, reason })
    })

    this.connection.on('error', () => {
      // 错误已通过 disconnected 事件处理
    })

    this.connection.on('message', (...args: unknown[]) => {
      const [msg] = args as [Message]
      this.handleMessage(msg)
    })
  }

  private setupSFUEvents(): void {
    this.sfu.on('remoteStream', (...args: unknown[]) => {
      const [stream] = args as [MediaStream]
      this.emit(Events.REMOTE_STREAM, stream)
    })

    this.sfu.on('iceCandidate', (...args: unknown[]) => {
      const [callId, candidate, sdpMid, sdpMLineIndex] = args as [string, string, string, number]
      this.connection.send({
        type: MessageType.SFU_ICE,
        data: { call_id: callId, candidate, sdp_mid: sdpMid, sdp_mline_index: sdpMLineIndex },
      })
    })

    this.sfu.on('offer', (...args: unknown[]) => {
      const [callId, sdp] = args as [string, string]
      this.connection.send({
        type: MessageType.SFU_OFFER,
        data: { call_id: callId, sdp },
      })
    })

    this.sfu.on('answer', (...args: unknown[]) => {
      const [callId, sdp] = args as [string, string]
      this.connection.send({
        type: MessageType.SFU_ANSWER,
        data: { call_id: callId, sdp },
      })
    })

    this.sfu.on('stateChange', (...args: unknown[]) => {
      const [state] = args as [RTCPeerConnectionState]
      if (state === 'connected') {
        this._callState = 'connected'
      }
      this.emit(Events.SFU_CONNECTION_STATE, state)

      // ICE 彻底失败且无法自恢复：结束通话并清理本地状态，
      // 避免本地停留在"已连接"假状态、服务端残留孤儿 call
      if (state === 'failed' && this._activeCall) {
        console.warn('[SDK] SFU connection failed, hanging up call:', this._activeCall.call_id)
        this.hangup(this._activeCall.call_id, 'sfu_connection_failed')
      }
    })

    this.sfu.on('error', (...args: unknown[]) => {
      const [error] = args as [Error]
      this.emit(Events.ERROR, { code: -1, message: error.message })
    })
  }

  private handleMessage(msg: Message): void {
    switch (msg.type) {
      case MessageType.ACK: {
        const data = msg.data as { original_type: string; success: boolean; message?: string; uid?: number }
        if (data.original_type === MessageType.REGISTER && data.success) {
          this._isRegistered = true
          if (data.uid) {
            this._uid = data.uid
          }
          this.emit(Events.REGISTERED, undefined)
          // 重连后恢复通话状态
          this.restoreAfterReconnect()
        }
        this.emit(Events.ACK, data)
        break
      }

      case MessageType.CALL: {
        const data = msg.data as { call_id: string; device_id: string; device_name: string; group?: string }
        // 新告警到来，清除旧的 activeCall 和 sfuIceServers
        if (this._activeCall && this._activeCall.call_id !== data.call_id) {
          console.log('[SDK] New incoming call, clearing old activeCall:', this._activeCall.call_id)
          this._activeCall = null
          this._sfuIceServers = []
          this.sfu.leave()
        }
        this._incomingCall = {
          call_id: data.call_id,
          operator_id: '',
          operator_name: '',
        }
        // 附加设备信息
        ;(this._incomingCall as any).device_id = data.device_id
        ;(this._incomingCall as any).device_name = data.device_name
        this.emit(Events.INCOMING_CALL, this._incomingCall as any)
        break
      }

      case MessageType.INCOMING_CALL: {
        const data = msg.data as { call_id: string; operator_id: string; operator_name: string }
        this._incomingCall = data
        this.emit(Events.INCOMING_CALL, data)
        break
      }

      case MessageType.ACCEPT: {
        const data = msg.data as any
        // 如果有匹配的 activeCall，更新设备信息
        if (this._activeCall && this._activeCall.call_id === data.call_id) {
          this._activeCall = {
            ...this._activeCall,
            device_id: data.device_id || this._activeCall.device_id,
            device_name: data.device_name || this._activeCall.device_name,
            state: 'sfu_joining',
          }
        }
        this.emit(Events.ACCEPT, data)
        break
      }

      case MessageType.REJECT: {
        const data = msg.data as any
        this.handleRejectMessage(data)
        break
      }

      case MessageType.CALL_STATE: {
        const data = msg.data as { call_id: string; state: string; operator_id?: string; operator_uid?: number }
        this.handleCallState(data)
        this.emit(Events.CALL_STATE, data as any)
        break
      }

      case MessageType.HANGUP: {
        const data = msg.data as any
        this.handleHangupMessage(data)
        break
      }

      case MessageType.ERROR: {
        const data = msg.data as { code: number; message: string }
        this.handleErrorMessage(data)
        break
      }

      // ========== SFU 信令 ==========
      case MessageType.SFU_JOIN: {
        const data = msg.data as any
        this.emit(Events.SFU_JOIN, data)
        if (this.options.enableSFU) {
          this.handleSFUJoin(data)
        }
        break
      }

      case MessageType.SFU_OFFER: {
        const data = msg.data as { call_id: string; sdp: string }
        if (this.options.enableSFU) {
          this.sfu.handleOffer(data.call_id, data.sdp).catch((e) => {
            this.emit(Events.ERROR, { code: -1, message: `SFU offer 处理失败: ${e}` })
          })
          this._glareRetryPending = false
        }
        break
      }

      case MessageType.SFU_ANSWER: {
        const data = msg.data as { call_id: string; sdp: string }
        if (this.options.enableSFU) {
          this.sfu.handleAnswer(data.sdp).catch((e) => {
            this.emit(Events.ERROR, { code: -1, message: `SFU answer 处理失败: ${e}` })
          })
        }
        break
      }

      case MessageType.SFU_ICE: {
        const data = msg.data as { call_id: string; candidate: string; sdp_mid: string; sdp_mline_index: number }
        if (this.options.enableSFU) {
          this.sfu.addICECandidate(data.candidate, data.sdp_mid, data.sdp_mline_index).catch(() => {})
        }
        break
      }

      case MessageType.SFU_LEAVE: {
        this.sfu.leave()
        break
      }

      case MessageType.HEARTBEAT: {
        break
      }
    }
  }

  /**
   * 处理 CALL_STATE 消息
   * - 使用 operator_uid 判断是否是自己的操作
   * - 使用 call_id 匹配防止旧通话消息覆盖新状态
   */
  private handleCallState(data: { call_id: string; state: string; operator_id?: string; operator_uid?: number }): void {
    const callId = data.call_id
    const hasUID = data.operator_uid !== undefined && data.operator_uid !== null
    const isMyCall = hasUID
      ? data.operator_uid === this._uid
      : (!data.operator_id || data.operator_id === this.options.id)

    const hasIncoming = !!this._incomingCall
    const hasActive = !!this._activeCall
    const incomingMatches = hasIncoming && this._incomingCall!.call_id === callId
    const activeMatches = hasActive && this._activeCall!.call_id === callId

    const incomingClearable = !hasIncoming || incomingMatches
    const activeClearable = !hasActive || activeMatches

    switch (data.state) {
      case 'calling':
        // calling 是信息性状态，不设置 activeCall
        this._callState = 'calling'
        break

      case 'accepted':
        // 无论是否本人接听，通话已有人接：清除可清除的来电（被他人接听时来电横幅应消失）
        if (incomingClearable) {
          this._incomingCall = null
        }
        this._callState = 'accepted'
        break

      case 'negotiating':
        if (isMyCall) {
          // 从 incomingCall 获取设备信息
          const deviceId = this._incomingCall?.device_id || this._activeCall?.device_id || ''
          const deviceName = this._incomingCall?.device_name || this._activeCall?.device_name || ''

          this._activeCall = {
            call_id: callId,
            device_id: deviceId,
            device_name: deviceName,
            operator_id: this.options.id,
            direction: 'device_to_operator',
            state: 'sfu_joining',
            startedAt: new Date().toISOString(),
          }
          this._incomingCall = null
        }
        this._callState = 'negotiating'
        break

      case 'connected':
        this._callState = 'connected'
        if (this._activeCall && this._activeCall.call_id === callId) {
          this._activeCall = { ...this._activeCall, state: 'connected' }
        }
        break

      case 'ended':
      case 'cancelled':
        if (!isMyCall) {
          break
        }
        if (activeClearable) {
          this._activeCall = null
        }
        if (incomingClearable) {
          this._incomingCall = null
          this._sfuIceServers = []
          this.sfu.leave()
        }
        this._callState = data.state === 'cancelled' ? 'cancelled' : 'ended'
        break
    }
  }

  /**
   * 处理 REJECT 消息
   */
  private handleRejectMessage(data: any): void {
    const callId = data?.call_id
    if (!callId) return

    const hasIncoming = !!this._incomingCall
    const hasActive = !!this._activeCall
    const incomingMatches = hasIncoming && this._incomingCall!.call_id === callId
    const activeMatches = hasActive && this._activeCall!.call_id === callId
    const canClear = (!hasIncoming || incomingMatches) && (!hasActive || activeMatches)

    if (canClear) {
      this._incomingCall = null
      this._activeCall = null
      this._sfuIceServers = []
      this.sfu.leave()
      this._callState = 'idle'
      console.log('[SDK] Call rejected, state cleaned up:', data)
    } else {
      console.log('[SDK] REJECT for old call, ignoring:', callId)
    }
  }

  /**
   * 处理 HANGUP 消息
   */
  private handleHangupMessage(data: any): void {
    const callId = data?.call_id
    const hasIncoming = !!this._incomingCall
    const hasActive = !!this._activeCall
    const incomingMatches = hasIncoming && this._incomingCall!.call_id === callId
    const activeMatches = hasActive && this._activeCall!.call_id === callId
    const canClear = (!hasIncoming || incomingMatches) && (!hasActive || activeMatches)

    if (canClear) {
      this._activeCall = null
      this._incomingCall = null
      this._sfuIceServers = []
      this.sfu.leave()
      this._callState = 'idle'
      this.emit(Events.HANGUP, data)
      console.log('[SDK] Call ended by peer:', data)
    } else {
      console.log('[SDK] HANGUP for old call, ignoring (current incoming:', this._incomingCall?.call_id, 'active:', this._activeCall?.call_id, 'hangup:', callId, ')')
    }
  }

  /**
   * 处理 ERROR 消息
   */
  private handleErrorMessage(data: ErrorData): void {
    // Glare 恢复：结构化 error_type 优先，兼容旧版服务端的文案匹配
    const isGlare = data.error_type === 'glare' || (data.message?.includes('glare') ?? false)
    if (isGlare && this._activeCall && !this._glareRetryPending) {
      this._glareRetryPending = true
      console.warn('[SDK] SFU glare detected, will retry offer in 1s')
      const callId = this._activeCall.call_id
      setTimeout(() => {
        if (this._glareRetryPending && this._activeCall && this._activeCall.call_id === callId) {
          console.log('[SDK] retrying SFU offer after glare')
          this.sfu.recreateOffer(callId).catch((e) => {
            this.emit(Events.ERROR, { code: -1, message: `SFU glare 重试失败: ${e}` })
          })
        }
      }, 1000)
      // glare 由 SDK 内部处理，不再向应用抛出
      return
    }

    // 通话生命周期错误：本地状态无条件清理，避免卡死
    // - 410: call ended
    // - 404: call not found（限定该文案，避免误清 initiate_call 的 device not online）
    // - 409: accept 冲突（被其他接警员接听等）
    const isCallGone =
      data.code === 410 ||
      (data.code === 404 && (data.message?.includes('call not found') ?? false)) ||
      (data.code === 409 && (data.message?.includes('accept') ?? false))
    if (isCallGone && (this._activeCall || this._incomingCall)) {
      console.log('[SDK] Call lifecycle error, clearing call state:', data.code, data.message)
      this._activeCall = null
      this._incomingCall = null
      this._sfuIceServers = []
      this.sfu.leave()
      this._callState = 'idle'
    }

    this.emit(Events.ERROR, data)
  }

  /**
   * 处理 SFU_JOIN 消息
   */
  private async handleSFUJoin(data: SFUJoinNotifyData): Promise<void> {
    const callId = data.call_id
    if (!callId) return

    // 同一通话的 PC 仍存活（仅信令断线重连，媒体通道未断）：无需重建，
    // 同时避免重复 getUserMedia 与服务端 peer refCount 泄漏
    const session = this.sfu.sessionInfo
    if (session.pcAlive && session.callId === callId) {
      console.log('[SDK] SFU PC still alive for call, skipping rejoin:', callId)
      return
    }
    if (session.pcAlive && session.callId !== callId) {
      // 旧通话的 PC 未清理（如服务端未发 hangup），强制释放后再加入新通话
      console.log('[SDK] SFU PC belongs to old call, leaving first:', session.callId)
      this.sfu.leave()
    }

    // 更新 activeCall
    const existingActive = this._activeCall
    if (existingActive && existingActive.call_id !== callId) {
      console.log('[SDK] replacing old activeCall with new call:', callId, 'old:', existingActive.call_id)
      this._activeCall = null
    }

    if (!this._activeCall) {
      const isDevice = this.options.role === 'device'
      const fallback = this._incomingCall
      this._activeCall = {
        call_id: callId,
        // 服务端 sfu_join 不携带设备信息：设备端用自身 ID，接警员端从来电信息取
        device_id: isDevice ? this.options.id : (fallback?.device_id ?? ''),
        device_name: isDevice ? this.options.name : (fallback?.device_name ?? ''),
        direction: data.direction || 'device_to_operator',
        state: 'sfu_joining',
        startedAt: new Date().toISOString(),
      }
      console.log('[SDK] activeCall pre-created from sfu_join, direction:', data.direction)
    } else if (this._activeCall.call_id === callId) {
      this._activeCall = { ...this._activeCall, state: 'sfu_joining' }
    }

    // 清除 incomingCall
    this._incomingCall = null

    // 提取 ICE 服务器配置（sfu_join 下发优先，构造参数兜底）
    const iceServers: ICEServer[] = data.ice_servers ?? []
    this._sfuIceServers = iceServers
    const finalIceServers = iceServers.length > 0 ? iceServers : this.options.iceServers

    // 获取本地流：优先使用 setLocalStream 传入的流（由调用方管理生命周期）
    let stream = this._manualLocalStream
    let ownsStream = false
    let failReason = ''
    if (!stream || !stream.getTracks().some((t) => t.readyState === 'live')) {
      const result = await getLocalStream(this.options.mediaConstraints)
      ownsStream = true
      stream = result.stream
      if (result.degraded && result.stream) {
        // 显式通知降级，禁止静默降级（否则对端"无画面"难以定位）
        this.emit(Events.MEDIA_DEGRADED, { reason: result.reason ?? '视频不可用，已降级为纯音频' })
      }
      if (!result.stream) {
        failReason = result.reason ?? ''
      }
    }

    if (!stream) {
      this.emit(Events.ERROR, { code: -1, message: `无法获取本地媒体流: ${failReason}` })
      // 媒体不可用：结束通话，避免服务端留下孤儿 call 等待超时
      this.hangup(callId, 'no_media')
      return
    }

    this.emit(Events.LOCAL_STREAM, stream)

    // 通知后端加入 SFU
    this.connection.send({
      type: MessageType.SFU_JOIN,
      data: { call_id: callId },
    })

    // SFU join
    await this.sfu.join(callId, finalIceServers, stream, ownsStream)
  }

  /**
   * 重连后恢复通话状态
   * 延迟 500ms 等待注册 ACK 返回
   *
   * 断线期间本地 activeCall/incomingCall 状态保留，注册成功后重发 accept：
   * - 接警员：服务端识别"同一接警员重连"，重发 sfu_join（handleAccept 已有路径）
   * - 设备：服务端识别为设备重连恢复，重发 sfu_join（handleAccept 新增路径）
   * 若通话已结束/被他人接听，服务端返回 410/404/409，由 handleErrorMessage 清理本地状态
   */
  private restoreAfterReconnect(): void {
    if (this._reconnectRestoreTimer) {
      clearTimeout(this._reconnectRestoreTimer)
    }

    this._reconnectRestoreTimer = setTimeout(() => {
      this._reconnectRestoreTimer = null
      if (!this._activeCall) return

      console.log('[SDK] Restoring active call after reconnection:', this._activeCall.call_id)
      this.accept(this._activeCall.call_id)
    }, 500)
  }
}